<template>
        <EditingDialog
        :context="context"
        :showSnackbar="true"
        snackbarTextLangConst="lang_GENERAL_SNACKBAR_TEXT_CLICK_MAIN_SAVE_BUTTON"
        :useDifferentShowDialogProp="true"
        :useAdditionalSaveFunction="true"
        :additionalSaveFunction="formatValuesBeforeSave"
        :additionalAfterSaveFunction="saveCosts"
    >
        <template v-slot:editForm>
            <v-row>
                <v-col>
                    <BaseEditPageDropdown 
                        :context="context"
                        field="CostGroup"
                        :items="costGroupItems"
                        :label="$t('lang_LABEL_CONTRACTS_CAPTION_COST_GROUP')"
                        :selection="true"
                        rules="required"
                        :withMargin="false"
                        :clearable="false"
                        :alvqa="alvqa + '-dropdown-cost-group'"
                    />
                </v-col>
                <v-col>
                    <BaseEditPageTextField 
                        :context="context"
                        field="Amount"
                        :label="$t('lang_GENERIC_CAPTION_AMOUNT')"
                        :withMargin="false"
                        rules="required|amountMaxTwoDigits"
                        :suffix="$store.getters.getSelectedCurrency"
                        :alvqa="alvqa + '-textfield-amount'"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <BaseEditPageDatePicker 
                        :context="context"
                        field="Date"
                        :label="$t('lang_GENERIC_CAPTION_DATE')"
                        :withMargin="false"
                        :rules="isFieldMandatory"
                        :alvqa="alvqa + '-datepicker-date'"
                    />
                </v-col>
                <v-col>
                    <BaseEditPageDatePicker 
                        :context="context"
                        field="PaidOn"
                        :label="$t('lang_LABEL_CONTRACTS_CAPTION_PAID_ON')"
                        :withMargin="false"
                        :alvqa="alvqa + '-datepicker-paid-on'"
                    />
                </v-col>
            </v-row>
            <v-row>
               <v-col>
                    <BaseEditPageAutocomplete 
                        :context="context"
                        field="Recipient"
                        :label="$t('lang_GENERIC_CAPTION_RECIPIENT')"
                        :withFreeText="false"
                        :withMargin="false"
                        :rules="isFieldMandatory"
                        :alvqa="alvqa + '-autocomplete-recipient-textfield'"
                    >
                        <template v-slot:selection="{ item }">
                            <div class="ellipsis" :alvqa="alvqa + '-autocomplete-recipient-selection'">
                                {{ item.gemaName }}
                                <span v-if="$alvPlugin.printName(item)">
                                    ({{ item.firstName }} {{ item.lastName }})
                                </span>
                            </div>
                        </template>
                        <template v-slot:items="{ items }">
                            <v-list-item-content :alvqa="alvqa + '-autocomplete-recipient-dropdown-' + items.alvId">
                                <v-list-item-title :alvqa="alvqa + '-autocomplete-recipient-dropdown-' + items.alvId + '-title'">
                                    {{ items.gemaName }}
                                    {{ $alvPlugin.printName(items) ? '(' + items.firstName + ' ' + items.lastName +')' : ''}}
                                        <v-avatar color="grey" size="20" class="caption">{{ items.typeOfAz }}</v-avatar>
                                </v-list-item-title>
                                <v-list-item-subtitle :alvqa="alvqa + '-autocomplete-recipient-dropdown-' + items.alvId + '-subtitle'">
                                    {{ $t('lang_IPI') }}: {{ items.ipi !== '' ? items.ipi : $t('lang_CAPTION_NONE') }} | {{ items.alvId }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </BaseEditPageAutocomplete>
                </v-col>
            </v-row>
            <v-row>
                <BaseEditPageDropdown 
                    :context="context"
                    field="AssignToWork"
                    :items="assignedWorksDropdownValues"
                    :label="$t('lang_PUBLISHING_CONTRACTS_CAPTION_COST_WORK_RELATION')"
                    :alvqa="alvqa + '-dropdown-assign-to-work'"
                    :return-object="true"
                    item-text="text"
                    item-value="alvId"
                />
            </v-row>
            <v-row>
                <v-col>
                    <BaseEditPageTextField 
                        :context="context"
                        field="Description"
                        :label="$t('lang_LABEL_CONTRACTS_CAPTION_DESCRIPTION')"
                        :withMargin="false"
                        :alvqa="alvqa + '-textfield-description'"
                    />
                </v-col>
                <v-col>
                    <BaseEditPageTextField 
                        :context="context"
                        field="DocumentNumber"
                        :label="$t('lang_LABEL_RELEASES_CAPTION_DOCUMENT_NUMBER')"
                        :withMargin="false"
                        :alvqa="alvqa + '-textfield-documentNumber'"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <BaseEditPageDropdown 
                        :label="$t('lang_GENERAL_CAPTION_COUNTRY')" 
                        :items="countryItems" 
                        :context="context"
                        field="Country"
                        :rules="isFieldMandatory"
                        :alvqa="alvqa + '-dropdown-country'"
                        :withMargin="false"
                    />
                </v-col>
                <v-col>
                    <BaseEditPageTextField 
                        :context="context"
                        field="Split"
                        :label="$t('lang_GENERAL_CAPTION_SPLIT')"
                        :withMargin="false"
                        rules="percentDecimal"
                        :alvqa="alvqa +'-textfield-split'"
                        suffix="%"
                    />
                </v-col>
            </v-row>
            <v-row class="pl-4">
                <BasePeriod 
                    :context="context+'AccountingPeriod'"
                    periodLabel="lang_LABEL_CONTRACTS_CAPTION_ACCOUNTING_PERIOD"
                    :undefinedTimeUnit="true"
                    :widthNumberField="2"
                    :clearable="false"
                />
            </v-row>
        </template>
    </EditingDialog>
</template>

<script>
import { DropdownPublishingContractsCostGroupTypes } from '@/assets/dropdowns/alvDropdowns.js'

import EditingDialog from '@/components/_dialogs/EditingDialog'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'
import BaseEditPageDatePicker from '@/components/_editForm/BaseEditPageDatePicker'
import BaseEditPageTextField from '@/components/_editForm/BaseEditPageTextField'
import BaseEditPageAutocomplete from '@/components/_editForm/BaseEditPageAutocomplete'
import BasePeriod from '@/components/_base/BasePeriod'

export default {
    name: "PublishingContractsCostsEditPanelDialog",
    components: {
        EditingDialog,
        BaseEditPageDropdown,
        BaseEditPageDatePicker,
        BaseEditPageTextField,
        BaseEditPageAutocomplete,
        BasePeriod
    },
    data() {
        return {
            context: 'PublishingContractsCosts',
            alvqa: "contracts-publishing-edit-form-panel-costs-editing-dialog"
        }
    },
    computed: {
        isFieldMandatory() {            
            let selectedAssignToWork = this.$store.getters.getPublishingContractsCostsDetailsAssignToWork
            return selectedAssignToWork !== null ? 'required' : ''
        },
        assignedWorksDropdownValues() {
            return this.$store.getters.getPublishingContractsCostsAssignToWorkOptions
        },
        costGroupItems() {
            let types = JSON.parse(JSON.stringify(DropdownPublishingContractsCostGroupTypes))
            types.forEach(element => {
                element.text = this.$t(element.text)
            })
            types.sort((a,b) => (a.text > b.text) ? 1 : (b.text > a.text) ? -1 : 0)
            return types
        },
        countryItems() { return this.$alvPlugin.getListOfTranslatedCountries() },
    },
    methods: {
        formatValuesBeforeSave(item) {
            item.amount = this.$alvPlugin.stringToFloat(item.amount)
            if(item.split !== null) item.split = this.$alvPlugin.stringToFloat(item.split)
            return item
        },
        saveCosts() {
            this.$store.dispatch('addPublishingContractsCosts')
        },
    },
    mounted() {
        this.$store.commit('setCountries')
    }
}
</script>
